import { getToken, isSupported, Messaging } from 'firebase/messaging'
import {
  API_KEY,
  APP_ID,
  MEASUREMENT_ID,
  MESSAGING_SENDER_ID,
  PROJECT_ID,
  VAPID_KEY,
} from 'features/firebase/Firebase.constants'
import { FirebaseApp, getApp, initializeApp } from 'firebase/app'
import { Maybe, SimpleMap } from '@vendorflow/types'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { CHATBOT_IDENTITY_ID } from '@vendorflow/common/lib/constants/Workspaces.constants'
import { handleAttachmentRefFormat } from 'services/Utils.service'
import { isTestEnv } from 'constants/Utils.constants'
import { forEach } from 'lodash'
import { SwitchboardEndpointsEnum } from 'features/switchboard/Switchboard.types'
import { useMyOrgId } from 'queries/Auth.queries'
import { FirebaseTestConfig } from 'services/testing/FirebaseSetup/Firebase.constants'

export function getFirebaseConfig(orgId?: string) {
  if (isTestEnv) {
    return {
      apiKey: FirebaseTestConfig.API_KEY,
      projectId: FirebaseTestConfig.PROJECT_ID,
    }
  }

  return {
    apiKey: API_KEY,
    authDomain: `${PROJECT_ID}.firebaseapp.com`,
    databaseURL: orgId ? `https://vf-ws--${orgId}.firebaseio.com` : undefined,
    projectId: PROJECT_ID,
    storageBucket: `${PROJECT_ID}.appspot.com`,
    messagingSenderId: MESSAGING_SENDER_ID,
    appId: APP_ID,
    measurementId: MEASUREMENT_ID,
  }
}

/** this is invoked in the FirebaseProvider component */
export function initializeFirebase(envy: string) {
  const config = getFirebaseConfig(envy)
  return initializeApp(config, envy)
}

export async function signInToFirebase(app: FirebaseApp, token: string) {
  const auth = getAuth(app)
  return signInWithCustomToken(auth, token)
}

export async function getNotificationToken(messaging: Messaging) {
  const hasSupport = await isSupported()
  if (!hasSupport) {
    return null // if this isn't supported then we will return early with a `null` token
  }

  const serviceWorkerRegistration = await navigator.serviceWorker.getRegistration('/')

  if (!serviceWorkerRegistration) {
    console.log('Service worker registration not found')
    return null
  }

  try {
    return getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration })
  } catch (err) {
    console.log('An error occurred while retrieving token. ', err)
    return null
  }
}

/** Only meant for our non-default firebase apps i.e. the ones for our workspaces */
export function hasApp(appName?: Maybe<string>) {
  if (!appName) return false

  try {
    getApp(appName)
    return true
  } catch (err) {
    return false
  }
}

export function hasDefaultApp() {
  return hasApp()
}

export function isDefaultApp(appName: string) {
  return appName === '[DEFAULT]'
}

export function useAppName() {
  const envy = useMyOrgId()!

  return isTestEnv ? undefined : envy
}

/**
 * @description ensures that the medium is a SwitchboardEndpointEnum
 */

export function getValueForPath(path: string, root: SimpleMap<any>) {
  if (!path) return null

  let pointer: any = root
  const steps = path.split('/')
  steps.shift() // remove the empty string left by the leading slash

  forEach(steps, (property) => {
    if (pointer.hasOwnProperty(property)) {
      pointer = pointer[property]
    } else {
      pointer = null
      return false
    }
  })

  return pointer
}

/**
 * Firebase Realtime Database paths
 */

// Info
export function getIsConnectedPath() {
  return '.info/connected'
}

// Primitives
export function getChannelsPath() {
  return `/channels`
}

export function getChannelPath(channelId: Maybe<string>) {
  return `${getChannelsPath()}/${channelId}`
}

export function getUserPath(userId: Maybe<string>) {
  return `/users/${userId}`
}

export function getResourcePath() {
  return `/resources`
}

export function getConfigPath() {
  return `/config`
}

export function getConfigWorkspacePath() {
  return `${getConfigPath()}/workspace`
}

export function getBobStaleCursors(bob: Maybe<string>) {
  return `${getUserPath(bob)}/staleCursors`
}

export function getBobStaleCursorsForChannels(bob: Maybe<string>) {
  return `${getBobStaleCursors(bob)}/channels`
}

export function getContactsPath() {
  return '/contacts'
}

// User Paths
export function getUserLastReadCursors(userId: Maybe<string>) {
  return `${getUserPath(userId)}/cursors`
}

export function getUserLastReadCursorChannelsPath(userId: Maybe<string>) {
  return `${getUserLastReadCursors(userId)}/channels`
}

export function getUserCursorForChannelPath(userId: Maybe<string>, channelId: Maybe<string>) {
  return `${getUserLastReadCursorChannelsPath(userId)}/${channelId}`
}

export function getBobChannelsHaveBeenUpdated(bobId: Maybe<string>) {
  return `${getUserPath(bobId)}/_tmp/latestChannelTimestamp`
}

export function getLatestAliceLocationPath(aliceId: Maybe<string>) {
  return `${getUserPath(aliceId)}/latestLocation`
}

export function getAliceDeactivatedStatus(aliceId: Maybe<string>) {
  return `${getUserPath(aliceId)}/deactivated`
}

export function getAliceMediumTracker(aliceId: Maybe<string>) {
  return `${getUserPath(aliceId)}/mediumTracker`
}

export function getUserSignaturePath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/configurations/signature`
}

export function getContactGroupsDisplayOrderPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/layout/contactGroups/displayOrder`
}

export function getBobOptOutSuffixPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/configurations/optOutSuffix`
}

export function getBobsFavoriteToolPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/layout/tools/favorite`
}

export function getChannelFeedSortPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/layout/channelFeed/sortBy`
}

export function getReplaceSpecialCharsPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/replaceSpecialChars`
}

// Uses SwitchboardMediumsEnum
export function getAliceLatestIncomingTimestamps(aliceId: Maybe<string>) {
  return `${getUserPath(aliceId)}/latestIncoming`
}

export function getAliceLastWhatsAppMessageTimestampPath(aliceId: Maybe<string>) {
  return `${getAliceLatestIncomingTimestamps(aliceId)}/${SwitchboardEndpointsEnum.WHATSAPP}/timestamp`
}

export function getPinnedChannelsPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/pinnedChannels`
}

export function getBobStaleCursorForChannel(bob: Maybe<string>, channel: Maybe<string>) {
  return `${getBobStaleCursorsForChannels(bob)}/${channel}`
}

export function getUserActivityPath(userId: Maybe<string>) {
  return `${getUserPath(userId)}/activity`
}

// Channel Paths
export function getChannelCursorsPath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/cursors`
}

export function getChannelStatusesPath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/statuses`
}

/** @deprecated */
export function getDeprecatedChannelLatestMessageIdPath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/latestMessageId`
}

export function getChannelLatestMessageCursorPath(channelId: Maybe<string>) {
  return `${getChannelCursorsPath(channelId)}/latest`
}

export function getChannelRelevantCursorPath(channelId: Maybe<string>) {
  return `${getChannelCursorsPath(channelId)}/relevant`
}

export function getChannelNamePath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/name`
}

export function getChannelReadOnlyPath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/readOnly`
}

export function getEManifestCaptureFormValues(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/eManifest/entries/0`
}

export function getElizaChannelActivity(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/activeChatbots/${CHATBOT_IDENTITY_ID}`
}

export function getUserTypingInChannelPath(channelId: Maybe<string>) {
  return `${getChannelPath(channelId)}/typing/`
}

export function getChannelVoiceCallPath(channelId: Maybe<string>) {
  return `${getChannelStatusesPath(channelId)}/voiceCall`
}

/** deprecated */
export function getResolvedPath(channelId: Maybe<string>) {
  return `${getChannelCursorsPath(channelId)}/resolved`
}

// Status Paths
export function getClaimedChannelStatusPath(channelId: Maybe<string>) {
  return `${getChannelStatusesPath(channelId)}/claimed`
}

export function getResolvedStatusPath(channelId: Maybe<string>) {
  return `${getChannelStatusesPath(channelId)}/resolved`
}

// Scaffold Paths
export function getAliceChannelMediumPath(channelId: Maybe<string>) {
  return `/scaffold/defaults/channel/${channelId}/medium`
}

export function getWebAppVersion() {
  return '/scaffold/webApp/version'
}

export function getAssetExportedToTmsPath(attachmentId: string) {
  const id = attachmentId && handleAttachmentRefFormat(attachmentId)
  return `/scaffold/exportedToTms/assets/${id}`
}

// Config Paths
export function getTemplatesConfigPath() {
  return `${getConfigWorkspacePath()}/templates`
}

export function getWorkspaceSettingsPath() {
  return `${getConfigWorkspacePath()}/settings`
}

export function getAutoMarkResolvedPath() {
  return `${getWorkspaceSettingsPath()}/autoMarkResolved`
}

export function getCustomTemplatesPath() {
  return `${getConfigPath()}/templates/messages`
}

export function getCustomTemplateIdPath(templateId: Maybe<string>) {
  return `${getCustomTemplatesPath()}/${templateId}`
}

// Comm Settings Paths
export function getEnvyPhoneNumber() {
  return `${getWorkspaceSettingsPath()}/phoneNumber`
}

export function getAliceChannelMemberAssignmentRule() {
  return `${getWorkspaceSettingsPath()}/aliceChannelMembersAssignmentRule`
}

export function getStaleAlertsPath() {
  return `${getWorkspaceSettingsPath()}/staleAlerts`
}

export function getContactGroupLimitPath() {
  return `${getWorkspaceSettingsPath()}/contactGroupLimit`
}

export function getSupportflowPath() {
  return `${getWorkspaceSettingsPath()}/supportflow`
}

export function getNotificationSettingsPath() {
  return `${getWorkspaceSettingsPath()}/notifications`
}

export function getContactDeletionSettingPath() {
  return `${getWorkspaceSettingsPath()}/contactDeletion`
}

export function getLandingViewPath() {
  return `${getWorkspaceSettingsPath()}/landingView`
}

export function getEnvyMessagePrefixPath() {
  return `${getWorkspaceSettingsPath()}/messagePrefix`
}

export function getContactValidationsPath() {
  return `${getWorkspaceSettingsPath()}/contacts/validations`
}

export function getEnvyOptOutSuffixOverridePath() {
  return `${getWorkspaceSettingsPath()}/optOutSuffixOverride`
}

export function getAdminOnlyCreateDeleteSettingPath() {
  return `${getWorkspaceSettingsPath()}/contactGroups/adminOnlyCreateDelete`
}

export function getCodeLinksPath() {
  return `${getWorkspaceSettingsPath()}/codeLinks`
}

export function getMandatorySignaturesPath() {
  return `${getWorkspaceSettingsPath()}/mandatorySignatures`
}

// Resource Paths
export function getIdentityResourcePath(id: Maybe<string>) {
  return `${getResourcePath()}/identities/${id}`
}

export function getIdentityResourceTimestampPath(id: Maybe<string>) {
  return `${getIdentityResourcePath(id)}/timestamp`
}

export function getManagedIdentitiesResourcePath() {
  return `${getResourcePath()}/managedIdentities`
}

export function getPersonalizationPath() {
  return `${getWorkspaceSettingsPath()}/personalization`
}

// Attachment Paths
export function getAttachmentDownloadedPath(attachmentId: Maybe<string>, identityId: Maybe<string>) {
  // attachmentId will be URI encoded if it is a href value.
  const formattedAttachmentId = attachmentId && handleAttachmentRefFormat(attachmentId)
  return `/attachments/${formattedAttachmentId}/downloads/${identityId}/latest`
}

// Message Config Paths
export function getFlaggedMessagePath(channelId: Maybe<string>, messageId: Maybe<string>) {
  return `/messages/${channelId}/${messageId}/flagged`
}

// Contacts
export function getContactPath(id: Maybe<string>) {
  return `${getContactsPath()}/${id}`
}

export function getCurrentDestinationPath(id: Maybe<string>) {
  return `${getContactPath(id)}/currentDestination`
}
